import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import RippleButton from "../../components/common/RippleButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { getAllActiveCities } from "../city/services/city.services";
import {
  createHotel,
  getHotelByID,
  updateHotel,
} from "./services/hotel.services";
import { clearHotelDetails } from "./hotelSlice";
import { ICity } from "../city/cityModel";
import { findIndex } from "lodash";
import { IState } from "../state/stateModel";
import { ICountry } from "../country/countryModel";
import HotelContactArray from "./HotelContactArray";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import { ISector } from "../sector/sectorModel";
import { STARRATING, STATUSARRAY } from "../../_contstants/common";
import { getAllActiveSectors } from "../sector/services/sector.services";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateHotel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const HotelID = location.state?.HotelID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.hotel.loading);
  const HotelDetail = useAppSelector((state) => state.hotel.HotelDetail);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);
  // const VendorList = useAppSelector((state) => state.vendor.VendorsList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [HotelDetail, CityList]);

  useEffect(() => {
    if (HotelID) {
      dispatch(getHotelByID(HotelID));
    }
    return () => {
      dispatch(clearHotelDetails());
    };
  }, [HotelID]);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
  }, []);

  const handleSubmit = async (values: any) => {
    if (HotelID) {
      const editPayload = {
        ID: values.ID,
        HotelName: values.HotelName,
        HotelCode: values.HotelCode,
        HotelDisplayName: values.HotelDisplayName,
        HotelAddress: values.HotelAddress,
        LandMark: values.LandMark,
        CountryID: values.CountryID,
        StateID: values.StateID,
        CityID: values.CityID,
        SectorID: values.SectorID,
        LatitudeOfHotel: values.LatitudeOfHotel ? values.LatitudeOfHotel : "",
        LongitudeOfHotel: values.LongitudeOfHotel
          ? values.LongitudeOfHotel
          : "",
        MapURL: values.MapURL ? values.MapURL : "",
        StarRating: values.StarRating ? values.StarRating : null,
        PropertyType: values.PropertyType ? values.PropertyType : "",
        Inclusions: values.Inclusions ? values.Inclusions : "",
        Exclusions: values.Exclusions ? values.Exclusions : "",
        HotelInformation: values.HotelInformation
          ? values.HotelInformation
          : "",
        VendorID: values.VendorID ? values.VendorID : null,
        IsActive: values.IsActive === 1,
        HotelContacts: values.HotelContacts
          ? values.HotelContacts.map((contact: any) => {
              return {
                HotelID: contact.HotelID ? contact.HotelID : null,
                PersonName: contact.PersonName ? contact.PersonName : "",
                MobileNo: contact.MobileNo ? contact.MobileNo.toString() : "",
                EmailID: contact.EmailID ? contact.EmailID : "",
                AlternateMobileNo: contact.AlternateMobileNo
                  ? contact.AlternateMobileNo.toString()
                  : "",
                AlternateEmailID: contact.AlternateEmailID
                  ? contact.AlternateEmailID
                  : "",
                IsOwner: contact.IsOwner === 1,
              };
            })
          : [],
      };
      try {
        const response = await dispatch(updateHotel(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(clearHotelDetails());
          navigate("/hotel");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      const insertPayload = {
        HotelName: values.HotelName,
        HotelCode: values.HotelCode,
        HotelDisplayName: values.HotelDisplayName,
        HotelAddress: values.HotelAddress,
        LandMark: values.LandMark,
        CountryID: values.CountryID,
        StateID: values.StateID,
        CityID: values.CityID,
        SectorID: values.SectorID,
        LatitudeOfHotel: values.LatitudeOfHotel ? values.LatitudeOfHotel : "",
        LongitudeOfHotel: values.LongitudeOfHotel
          ? values.LongitudeOfHotel
          : "",
        MapURL: values.MapURL ? values.MapURL : "",
        StarRating: values.StarRating ? values.StarRating : null,
        PropertyType: values.PropertyType ? values.PropertyType : "",
        Inclusions: values.Inclusions ? values.Inclusions : "",
        Exclusions: values.Exclusions ? values.Exclusions : "",
        HotelInformation: values.HotelInformation
          ? values.HotelInformation
          : "",
        VendorID: values.VendorID ? values.VendorID : null,
        IsActive: values.IsActive === 1,
        HotelContacts: values.HotelContacts
          ? values.HotelContacts.map((contact: any) => {
              return {
                HotelID: contact.HotelID ? contact.HotelID : null,
                PersonName: contact.PersonName ? contact.PersonName : "",
                MobileNo: contact.MobileNo ? contact.MobileNo.toString() : "",
                EmailID: contact.EmailID ? contact.EmailID : "",
                AlternateMobileNo: contact.AlternateMobileNo
                  ? contact.AlternateMobileNo.toString()
                  : "",
                AlternateEmailID: contact.AlternateEmailID
                  ? contact.AlternateEmailID
                  : "",
                IsOwner: contact.IsOwner === 1,
              };
            })
          : [],
      };
      try {
        const response = await dispatch(createHotel(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(clearHotelDetails());
          navigate("/hotel");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={HotelDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {HotelID ? "Update Hotel" : "Add Hotel"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelName"
                    astrike={true}
                    label="Hotel Name"
                    placeholder="Hotel Name"
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelCode"
                    label="Hotel Code"
                    placeholder="Hotel Code"
                    component={FormTextField}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelDisplayName"
                    label="Hotel Display Name"
                    placeholder="Hotel Display Name"
                    component={FormTextField}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="HotelAddress"
                    label="Hotel Address"
                    placeholder="Hotel Address"
                    component={FormTextField}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="LandMark"
                    label="LandMark"
                    placeholder="LandMark"
                    component={FormTextField}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"SectorID"}
                    name={"SectorID"}
                    label={"Sector"}
                    placeholder={"Sector"}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={SectorList?.map((sector: ISector) => {
                      return {
                        value: sector?.ID,
                        label: sector?.SectorName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder={"City"}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder={"State"}
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={StateList?.map((state: IState) => {
                      return {
                        value: state?.ID,
                        label: state?.StateName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    placeholder={"Country"}
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="LatitudeOfHotel"
                    label="Latitude Of Hotel"
                    placeholder="0"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="LongitudeOfHotel"
                    label="Longitude Of Hotel"
                    placeholder="0"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="MapURL"
                    label="Map URL"
                    placeholder="Map URL"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name={"StarRating"}
                    label={"Star Rating"}
                    placeholder={"Star Rating"}
                    component={FormSelectionField}
                    options={STARRATING?.map((rating: any) => {
                      return {
                        value: rating?.value,
                        label: rating?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                {/* <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    id={"VendorID"}
                    name={"VendorID"}
                    label={"Vendor"}
                    component={FormSelectionField}
                    options={VendorList?.map((vendor: IVendors) => {
                      return {
                        value: vendor?.ID,
                        label: vendor?.VendorName,
                      };
                    })}
                  />
                </GridLayoutItem> */}
                <GridLayoutItem>
                  <Field
                    name="PropertyType"
                    label="Property Type"
                    placeholder="Property Type"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Inclusions"
                    label="Inclusions"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Exclusions"
                    label="Exclusions"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelInformation"
                    label="Hotel Information"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ marginTop: "10px" }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem style={{ marginTop: 10 }} colSpan={3}>
                  <FieldArray
                    formRenderProps={formRenderProps}
                    component={HotelContactArray}
                    name="HotelContacts"
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={HotelID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/hotel")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
              <CityChangeWatcher
                formRenderProps={formRenderProps}
                CityList={CityList}
              />
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateHotel;
